'use client';

import { motion, useInView } from 'motion/react';
import { useRef } from 'react';
import {
  MessageSquareText,
  Server,
  Calculator,
  FileText,
  Link2,
  LayoutDashboard,
  Shield,
  ArrowUpRight,
} from 'lucide-react';

const features = [
  {
    icon: MessageSquareText,
    title: 'Plain-English Guidance per IGP',
    description:
      'Every Indicator of Good Practice includes a plain-English explanation, OT-specific context, and common evidence examples — so your team knows exactly what is being assessed.',
    tag: 'Guidance',
    tagColor: 'bg-accent/10 text-accent',
    highlight: true,
  },
  {
    icon: Server,
    title: 'Multi-System Assessment',
    description:
      'Assess multiple OT systems within a single assessment, each with their own responses and status. Built for complex CNI infrastructure.',
    tag: 'Scale',
    tagColor: 'bg-[hsl(225_60%_45%/0.1)] text-[hsl(225_60%_45%)]',
  },
  {
    icon: Calculator,
    title: 'Automatic Status Rollup',
    description:
      'Status is calculated automatically from IGP level through Contributing Outcome, Principle, and Objective — based on the CAF logic, not manual formula entry.',
    tag: 'Automation',
    tagColor: 'bg-success/10 text-success',
  },
  {
    icon: FileText,
    title: 'PDF and Excel Export',
    description:
      'Generate structured PDF and Excel outputs on demand. Formatted for regulator submission — produced in seconds, not days.',
    tag: 'Export',
    tagColor: 'bg-warning/10 text-warning',
  },
  {
    icon: Link2,
    title: 'Evidence Tracking',
    description:
      'Reference policies, documents, and audit evidence against each IGP response. Build a traceable compliance record as the assessment progresses.',
    tag: 'Audit Trail',
    tagColor: 'bg-[hsl(225_60%_45%/0.1)] text-[hsl(225_60%_45%)]',
  },
  {
    icon: LayoutDashboard,
    title: 'Progress Dashboard',
    description:
      'Visual progress tracking across all four Objectives with quick navigation to incomplete areas, gaps, and outstanding responses.',
    tag: 'Visibility',
    tagColor: 'bg-accent/10 text-accent',
  },
];

export function FeaturesSection() {
  const containerRef = useRef(null);
  const isInView = useInView(containerRef, { once: true, margin: '-100px' });

  return (
    <section className="relative overflow-hidden py-24 lg:py-32" id="features">
      <div className="absolute inset-0 bg-background" />
      <div className="absolute inset-0 bg-dots opacity-30" />
      <div className="absolute left-1/2 top-0 h-[600px] w-[800px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-accent/5 blur-3xl" />

      <div
        ref={containerRef}
        className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8"
      >
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="mx-auto max-w-3xl text-center"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={isInView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.5 }}
            className="mb-6 inline-flex items-center gap-2 rounded-full border border-accent/20 bg-accent/5 px-4 py-1.5"
          >
            <Shield className="h-4 w-4 text-accent" />
            <span className="font-heading text-sm font-medium text-foreground">
              Platform Features
            </span>
          </motion.div>

          <h2 className="font-display text-3xl tracking-tight-display text-foreground sm:text-4xl lg:text-5xl">
            Everything the CAF assessment{' '}
            <span className="italic text-accent">requires</span>
          </h2>

          <p className="mt-6 text-lg text-muted-foreground">
            Each capability is built around the specific demands of a CAF 4.0
            assessment — from first IGP to final export.
          </p>
        </motion.div>

        {/* Features Grid */}
        <div className="mt-16 grid gap-4 sm:gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 30 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: 0.1 + index * 0.08 }}
                className={`card-hover group relative overflow-hidden rounded-2xl border bg-card p-6 lg:p-8 ${
                  feature.highlight
                    ? 'border-accent/30 ring-1 ring-accent/10'
                    : 'border-border'
                }`}
              >
                {feature.highlight && (
                  <div className="absolute right-4 top-4">
                    <span className="rounded-full bg-accent px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-accent-foreground">
                      Core
                    </span>
                  </div>
                )}

                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-secondary transition-all duration-300 group-hover:bg-accent/10">
                  <Icon
                    className="h-6 w-6 text-muted-foreground transition-colors duration-300 group-hover:text-accent"
                    strokeWidth={1.5}
                  />
                </div>

                <div className="mt-4">
                  <span
                    className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${feature.tagColor}`}
                  >
                    {feature.tag}
                  </span>
                </div>

                <h3 className="mt-4 font-heading text-xl font-semibold text-foreground">
                  {feature.title}
                </h3>

                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                  {feature.description}
                </p>

                <div className="mt-6 flex items-center gap-1.5 text-sm font-medium text-accent opacity-0 transition-all duration-300 group-hover:opacity-100">
                  Learn more
                  <ArrowUpRight className="h-4 w-4" />
                </div>

                <div className="absolute -bottom-12 -right-12 h-32 w-32 rounded-full bg-accent/5 transition-all duration-500 group-hover:scale-150" />
              </motion.div>
            );
          })}
        </div>

        {/* Bottom decoration */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="mt-16 text-center"
        >
          <div className="mx-auto inline-flex items-center gap-4 rounded-full border border-border bg-card px-6 py-3 shadow-sm">
            <div className="flex -space-x-2">
              {['A', 'B', 'C', 'D'].map((letter, i) => (
                <div
                  key={letter}
                  className="flex h-8 w-8 items-center justify-center rounded-full border-2 border-card bg-primary font-heading text-xs font-bold text-primary-foreground"
                  style={{ zIndex: 4 - i }}
                >
                  {letter}
                </div>
              ))}
            </div>
            <p className="text-sm text-muted-foreground">
              <span className="font-semibold text-foreground">All 4 Objectives</span>{' '}
              · 41 Contributing Outcomes · ~300 IGPs
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
